import React, { useState, useEffect } from 'react'
import login from '../../services/auth.service'
import { useForm } from 'react-hook-form'
import { useEyeconxStore } from '../../store/eyeconxStore'
import { useNavigate, Link } from 'react-router-dom'
import { toAbsoluteUrl } from '../helpers/AssetHelpers'
import './auth.css'

function Login() {
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm()

    const [message, setMessage] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const [rememberMe, setRememberMe] = useState(false)
    const setToken = useEyeconxStore((state) => state.setToken)
    const setUser = useEyeconxStore((state) => state.setUser)
    const setIsAuthenticate = useEyeconxStore((state) => state.setIsAuthenticate)
    const logout = useEyeconxStore((state) => state.logout)
    const user = useEyeconxStore((state) => state.user)
    const token = useEyeconxStore((state) => state.token)
    const isAuthenticated = useEyeconxStore((state) => state.isAuthenticated)

    const navigate = useNavigate()

    useEffect(() => {
        const savedCredentials = localStorage.getItem('rememberedCredentials')
        if (savedCredentials) {
            const { username, password } = JSON.parse(savedCredentials)
            setValue('username', username)
            setValue('password', password)
            setRememberMe(true)
        }
    }, [setValue])

    const onSubmit = async (data) => {
        setMessage(null)
        try {
            const res = await login(data.username, data.password)
            if (res.data.statusCode === 200) {
                const nextUser = {
                    ID: res.data.uId,
                    name: res.data.username,
                    role: res.data.role,
                    clinicId: res.data.clinicId,
                    authorization: 'Bearer ' + res.data.token,
                    clinicType: res.data.clinicType,
                }

                setToken(res.data.token)
                setUser(nextUser)
                setIsAuthenticate(true)

                if (rememberMe) {
                    localStorage.setItem(
                        'rememberedCredentials',
                        JSON.stringify({
                            username: data.username,
                            password: data.password,
                        })
                    )
                } else {
                    localStorage.removeItem('rememberedCredentials')
                }

                localStorage.removeItem('notifications')
            }
        } catch (error) {
            console.error(error)
            if (error?.response) {
                setMessage(error.response.data.error || 'Login failed. Please try again.')
            } else {
                setMessage('Network error. Please check your connection.')
            }
        }
    }

    useEffect(() => {
        if (isAuthenticated && token != null) {
            if (user.clinicType === 'EYECONX') {
                navigate('/dashboard')
            } else if (user.clinicType === 'SMS_GATEWAY') {
                navigate('/sms-dashboard')
            } else {
                logout()
                navigate('/login')
            }
        }
    }, [user, isAuthenticated, token, navigate, logout])

    return (
        <div className="login-page__card">
            <img
                src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                alt="EyeconX"
                className="login-page__form-logo"
            />

            <div className="login-page__header">
                <h2 className="login-page__title">Welcome back</h2>
                <p className="login-page__subtitle">
                    Sign in to your clinic account to continue
                </p>
            </div>

            {message && (
                <div className="login-page__alert" role="alert">
                    <i className="bi bi-exclamation-circle-fill" />
                    <span>{message}</span>
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="login-page__field">
                    <label htmlFor="username" className="login-page__label">
                        Username
                    </label>
                    <div className="login-page__input-wrap">
                        <i className="bi bi-person login-page__input-icon" />
                        <input
                            type="text"
                            id="username"
                            className={`login-page__input ${errors.username ? 'login-page__input--invalid' : ''}`}
                            {...register('username', { required: 'Username is required' })}
                            placeholder="Enter your username"
                            autoComplete="username"
                            tabIndex={1}
                        />
                    </div>
                    {errors.username && (
                        <div className="login-page__error">
                            <i className="bi bi-exclamation-circle" />
                            {errors.username.message}
                        </div>
                    )}
                </div>

                <div className="login-page__field">
                    <label htmlFor="password" className="login-page__label">
                        Password
                    </label>
                    <div className="login-page__input-wrap">
                        <i className="bi bi-lock login-page__input-icon" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            className={`login-page__input login-page__input--password ${errors.password ? 'login-page__input--invalid' : ''}`}
                            {...register('password', { required: 'Password is required' })}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            tabIndex={2}
                        />
                        <button
                            type="button"
                            className="login-page__toggle-password"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            tabIndex={-1}
                        >
                            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                        </button>
                    </div>
                    {errors.password && (
                        <div className="login-page__error">
                            <i className="bi bi-exclamation-circle" />
                            {errors.password.message}
                        </div>
                    )}
                </div>

                <div className="login-page__options">
                    <label className="login-page__checkbox" htmlFor="rememberMe">
                        <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <span className="login-page__checkbox-label">Remember me</span>
                    </label>
                    <Link to="/forgot-password" className="login-page__forgot">
                        Forgot Password?
                    </Link>
                </div>

                <button
                    type="submit"
                    className="login-page__submit"
                    disabled={isSubmitting}
                    tabIndex={3}
                >
                    {isSubmitting ? (
                        <>
                            <span className="login-page__spinner" role="status" aria-hidden="true" />
                            Signing in...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-box-arrow-in-right" />
                            Sign in
                        </>
                    )}
                </button>
            </form>

            <div className="login-page__footer-wrap">
                <p className="login-page__meta">
                    Need help?{' '}
                    <a href="mailto:support@eyeconx.net">Contact support</a>
                </p>
                <span className="login-page__badge">
                    <i className="bi bi-building" />
                    Clinic access
                </span>
            </div>
        </div>
    )
}

export default Login
